import { useState } from "react";
import { motion } from "framer-motion";
import { X, Pencil, Trash2, Plus } from "lucide-react";
import { CATEGORIES } from "../data/products";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_OPTIONS = CATEGORIES.filter((c) => c.key !== "all").map((c) => c.key);
const EMPTY = { title: "", category: CATEGORY_OPTIONS[0], price: "", rating: "", reviews: "", badge: "", image: "", link: "", description: "" };

const Field = ({ label, testid, ...props }) => (
  <label className="flex flex-col gap-1.5">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</span>
    <input
      data-testid={testid}
      {...props}
      className="rounded-xl border border-white/10 bg-[#0B0C0E] px-4 py-2.5 text-sm text-slate-100 outline-none transition-colors focus:border-[#FF9900]/60"
    />
  </label>
);

export const AdminPanel = ({ products, onClose, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const startAdd = () => {
    setForm(EMPTY);
    setEditing({ isNew: true });
  };

  const startEdit = (p) => {
    setForm({
      title: p.title, category: p.category, price: p.price,
      rating: String(p.rating), reviews: String(p.reviews),
      badge: p.badge, image: p.image, link: p.link, description: p.description,
    });
    setEditing(p);
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, rating: parseFloat(form.rating) || 0, reviews: parseInt(form.reviews, 10) || 0 };
    const isNew = !!editing?.isNew;
    await fetch(`${BACKEND_URL}/api/admin/products${isNew ? "" : `/${editing.id}`}`, {
      method: isNew ? "POST" : "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    }).catch(() => {});
    setSaving(false);
    setEditing(null);
    onChanged();
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await fetch(`${BACKEND_URL}/api/admin/products/${p.id}`, { method: "DELETE", credentials: "include" }).catch(() => {});
    onChanged();
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <motion.div
      data-testid="admin-panel"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] overflow-y-auto bg-[#0B0C0E]/95 backdrop-blur-xl"
    >
      <div className="mx-auto max-w-5xl px-5 py-10 md:px-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FFB800]">Owner only</p>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-50">
              Manage Catalog <span className="font-mono text-lg text-slate-500">({products.length})</span>
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {!editing && (
              <button
                data-testid="admin-add-product-btn"
                onClick={startAdd}
                className="inline-flex items-center gap-2 rounded-full bg-[#FF9900] px-4 py-2 text-xs font-bold text-[#0B0C0E] transition-colors hover:bg-[#FFB800] active:scale-95"
              >
                <Plus size={14} /> Add Product
              </button>
            )}
            <button
              data-testid="admin-close-btn"
              onClick={onClose}
              aria-label="Close admin panel"
              className="rounded-full border border-white/10 bg-[#121418] p-2.5 text-slate-300 transition-colors hover:bg-[#FF9900] hover:text-[#0B0C0E]"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {editing ? (
          <div className="mt-10 rounded-2xl border border-white/10 bg-[#121418] p-6 md:p-8" data-testid="admin-product-form">
            <h3 className="font-display text-lg font-semibold text-slate-50">
              {editing.isNew ? "Add new product" : `Edit: ${editing.title}`}
            </h3>
            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
              <Field label="Title" testid="admin-input-title" value={form.title} onChange={set("title")} />
              <label className="flex flex-col gap-1.5">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Category</span>
                <select
                  data-testid="admin-input-category"
                  value={form.category}
                  onChange={set("category")}
                  className="rounded-xl border border-white/10 bg-[#0B0C0E] px-4 py-2.5 text-sm text-slate-100 outline-none focus:border-[#FF9900]/60"
                >
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
              <Field label="Price (e.g. ₹999)" testid="admin-input-price" value={form.price} onChange={set("price")} />
              <Field label="Badge (e.g. Best Seller)" testid="admin-input-badge" value={form.badge} onChange={set("badge")} />
              <Field label="Rating (e.g. 4.5)" testid="admin-input-rating" type="number" step="0.1" value={form.rating} onChange={set("rating")} />
              <Field label="Reviews count" testid="admin-input-reviews" type="number" value={form.reviews} onChange={set("reviews")} />
              <Field label="Image URL" testid="admin-input-image" value={form.image} onChange={set("image")} />
              <Field label="Amazon affiliate link" testid="admin-input-link" value={form.link} onChange={set("link")} />
              <label className="flex flex-col gap-1.5 md:col-span-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Description</span>
                <textarea
                  data-testid="admin-input-description"
                  value={form.description}
                  onChange={set("description")}
                  rows={3}
                  className="rounded-xl border border-white/10 bg-[#0B0C0E] px-4 py-2.5 text-sm text-slate-100 outline-none focus:border-[#FF9900]/60"
                />
              </label>
            </div>
            <div className="mt-8 flex gap-3">
              <button
                data-testid="admin-save-btn"
                onClick={save}
                disabled={saving || !form.title || !form.link}
                className="rounded-full bg-[#FF9900] px-6 py-2.5 text-sm font-bold text-[#0B0C0E] transition-colors hover:bg-[#FFB800] disabled:opacity-40 active:scale-95"
              >
                {saving ? "Saving…" : "Save Product"}
              </button>
              <button
                data-testid="admin-cancel-btn"
                onClick={() => setEditing(null)}
                className="rounded-full border border-white/15 px-6 py-2.5 text-sm font-semibold text-slate-300 transition-colors hover:border-[#FF9900]/50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-10 flex flex-col gap-3">
            {products.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-product-row-${p.id}`}
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#121418] p-4"
              >
                <img src={p.image} alt={p.title} className="h-14 w-14 shrink-0 rounded-xl object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-sm font-semibold text-slate-100">{p.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    {p.category} · <span className="text-[#FFB800]">{p.price}</span>
                  </p>
                </div>
                <button
                  data-testid={`admin-edit-btn-${p.id}`}
                  onClick={() => startEdit(p)}
                  aria-label={`Edit ${p.title}`}
                  className="rounded-full border border-white/10 p-2.5 text-slate-300 transition-colors hover:border-[#FF9900]/50 hover:text-[#FF9900]"
                >
                  <Pencil size={14} />
                </button>
                <button
                  data-testid={`admin-delete-btn-${p.id}`}
                  onClick={() => remove(p)}
                  aria-label={`Delete ${p.title}`}
                  className="rounded-full border border-white/10 p-2.5 text-slate-300 transition-colors hover:border-red-400/60 hover:text-red-400"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};
